import { Box, styled } from '@mui/material';

import { NativeVaultColor } from '@/theme/colorSchemes';

export const resolveSnapshotBgColor = (
  backgroundColor: string | null,
  isNative?: boolean
): string | null => backgroundColor ?? (isNative ? NativeVaultColor : null);

/**
 * How completely a snapshot captured its subject. Named level to keep it distinct from the SDK's
 * SnapshotCoverage, which is the object-store byte breakdown rather than a verdict.
 */
export type SnapshotCoverageLevel = 'complete' | 'partial' | 'failed';

export const SnapshotIndicator = (props: {
  backgroundColor: string | null;
  /** How completely the snapshot captured the SaaS entity being browsed. */
  coverage?: SnapshotCoverageLevel;
  /** The resource snapshot skipped items it could not access. */
  isPartial?: boolean;
  isNative?: boolean;
  sx?: object;
}) => {
  const bgColor = resolveSnapshotBgColor(props.backgroundColor, props.isNative);
  if (props.coverage === 'failed') {
    return <FailedSnapshotIndicator sx={props.sx} />;
  }
  return props.coverage === 'partial' || props.isPartial ? (
    <PartialSnapshotIndicator backgroundColor={bgColor} sx={props.sx} />
  ) : (
    <FullSnapshotIndicator backgroundColor={bgColor} sx={props.sx} />
  );
};

const FullSnapshotIndicator = styled('div')(
  (props: { backgroundColor: string | null; sx?: object }) => ({
    height: '12px',
    width: '12px',
    borderRadius: '50%',
    display: 'inline-block',
    minWidth: '12px',
    minHeight: '12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: props.backgroundColor || 'gray',
    backgroundColor: props.backgroundColor || 'transparent',
    ...props.sx,
  })
);

// Deliberately carries no vault colour. The other dots answer "where is this snapshot kept";
// this one answers "this backup failed", which the vault palette must not soften.
const FailedSnapshotIndicator = (props: { sx?: object }) => {
  return (
    <Box
      component='svg'
      viewBox='0 0 12 12'
      role='img'
      aria-label='Failed'
      sx={{
        height: '12px',
        width: '12px',
        minWidth: '12px',
        minHeight: '12px',
        display: 'inline-block',
        ...props.sx,
      }}
    >
      <circle
        cx='6'
        cy='6'
        r='5.5'
        fill='var(--mui-palette-background-paper)'
        stroke='var(--mui-palette-error-main)'
      />
      <path
        d='M6 3.1V6.7'
        stroke='var(--mui-palette-error-main)'
        strokeWidth='1.4'
        strokeLinecap='round'
      />
      <circle cx='6' cy='8.7' r='0.85' fill='var(--mui-palette-error-main)' />
    </Box>
  );
};

const PartialSnapshotIndicator = (props: {
  backgroundColor: string | null;
  sx?: object;
}) => {
  return (
    <Box
      role='img'
      aria-label='Partial'
      sx={{
        height: '12px',
        width: '12px',
        borderRadius: '50%',
        display: 'inline-block',
        minWidth: '12px',
        minHeight: '12px',
        border: `2px solid ${props.backgroundColor}`,
        overflow: 'hidden',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          marginTop: '-1.5px',
          marginLeft: '-3px',
          height: '5px',
          width: '12px',
          transform: 'rotate(-45deg)',
          backgroundColor: props.backgroundColor,
        }}
      ></Box>
    </Box>
  );
};
